import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import Header from "../../../components/Header";
import { accountsmanagement } from "../../../config";
import { graphqlQuery, generateAndExecuteMutation } from "../../../data/Axios/DynamicService";
import { allAccountSubCategories, allTaxRates } from "../../../data/Axios/queries";

const base_url = accountsmanagement.uri;

const ClientReceiptLinesForm = (props) => {
  //const { isEditing = false, data = {}, onClose, refetchData } = props;

  const [initialValues, setInitialValues] = useState({
    id: props.data ? props.data.id : null,
    receiptId: props.data ? props.data.id : "",
    description: props.data ? props.data.description: "",
    accountSubCategoryId: props.data ? props.data.accountSubCategoryId : "",
    amount: props.data ? props.data.amount :  0,
    vatId: props.data ? props.data.vatId : "",
    vatAmount: props.data ? props.data.vatAmount: 0,
    isActive: props.data ? props.data.isActive : 1,
  });

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;
  const receiptData = JSON.parse(localStorage.getItem("MyClientReceiptData") || "{}");
  const receiptId = receiptData?.id || null;

  const [accountSubCategories, setAccountSubCategories] = useState([]);
  const [taxRates, setTaxRates] = useState([]);

  // Fetch master data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch account subcategories and filter for IDs 34 and 37
        const subCategoryData = await graphqlQuery(allAccountSubCategories, base_url);
        const filteredSubCategories = (subCategoryData || []).filter(
          (subCat) => [34, 37].includes(subCat.id)
        );
        setAccountSubCategories(filteredSubCategories);

        // Fetch tax rates and filter for taxTypeId = 1
        const taxRateData = await graphqlQuery(allTaxRates, base_url);
        const filteredTaxRates = (taxRateData || []).filter(
          (tax) => tax.taxTypeId === 1
        );
        setTaxRates(filteredTaxRates);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Map backend fields and set initial values
  useEffect(() => {
    if (props.data && props.isEditing) {
      const mappedData = {
        id: props.data.id || null,
        receiptId: props.data.receiptId || receiptId,
        description: props.data.description || "",
        accountSubCategoryId: props.data.accountSubCategoryId || "",
        amount: props.data.amount || 0,
        vatId: props.data.vatId || "",
        vatAmount: props.data.vatAmount || 0,
        isActive: props.data.isActive || 1,
      };
      setInitialValues(mappedData);
    } else {
      setInitialValues((prev) => ({
        ...prev,
        receiptId: receiptId,
      }));
    }
  }, [props.data, props.isEditing, receiptId]);

  // Validation schema
  const validationSchema = yup.object().shape({
    description: yup.string().required("Description is required"),
    accountSubCategoryId: yup.string().required("Account Subcategory is required"),
    amount: yup.number().min(0, "Amount must be positive").required("Amount is required"),
    vatId: yup.string().required("VAT is required"),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Calculate VAT amount based on amount and selected VAT rate
  const calculateVatAmount = (amount, vatId) => {
    const selectedTax = taxRates.find((tax) => tax.id === parseInt(vatId));
    if (!selectedTax || !amount) return 0;
    return (parseFloat(amount) * (selectedTax.rate / 100)).toFixed(2);
  };

  // Submit handler
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = {
        ...values,
        receiptId: parseInt(receiptId),
        accountSubCategoryId: parseInt(values.accountSubCategoryId),
        amount: parseFloat(values.amount),
        vatId: parseInt(values.vatId),
        vatAmount: parseFloat(calculateVatAmount(values.amount, values.vatId)),
      };

      const finalMutationData = props.isEditing
        ? { id: props.data?.id, ...mutationData }
        : { ...mutationData, createdBy: userId };

      const mutationName = props.isEditing ? "updateReceiptLine" : "createReceiptLine";
      const responseKey = props.isEditing ? "updateReceiptLines" : "newReceiptLine";

      const response = await generateAndExecuteMutation(
        mutationName,
        finalMutationData,
        responseKey,
        base_url
      );

      if (response) {
        props.onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Receipt Line ${props.isEditing ? "updated" : "created"} successfully.`,
        }).then(() => {
          props.refetchData();
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong while saving the receipt line.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Line" : "Create Line"}
        subtitle={props.isEditing ? "Edit existing Line" : "Create new Line"}
      />

      <Formik
        onSubmit={handleSubmit}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Description */}
              <TextField
                fullWidth
                variant="filled"
                label="Description *"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
              />

              {/* Account Subcategory */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Account Subcategory *</InputLabel>
                <Select
                  name="accountSubCategoryId"
                  value={values.accountSubCategoryId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountSubCategoryId && !!errors.accountSubCategoryId}
                >
                  {accountSubCategories.map((subCat) => (
                    <MenuItem key={subCat.id} value={subCat.id}>
                      {subCat.accountCategoryName}
                    </MenuItem>
                  ))}
                </Select>
                {touched.accountSubCategoryId && errors.accountSubCategoryId && (
                  <Typography color="error">{errors.accountSubCategoryId}</Typography>
                )}
              </FormControl>

              {/* Amount */}
              <TextField
                fullWidth
                variant="filled"
                label="Amount *"
                name="amount"
                type="number"
                value={values.amount}
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue("vatAmount", calculateVatAmount(e.target.value, values.vatId));
                }}
                onBlur={handleBlur}
                error={touched.amount && !!errors.amount}
                helperText={touched.amount && errors.amount}
              />

              {/* VAT */}
              <FormControl fullWidth variant="filled">
                <InputLabel>VAT *</InputLabel>
                <Select
                  name="vatId"
                  value={values.vatId}
                  onChange={(e) => {
                    handleChange(e);
                    setFieldValue("vatAmount", calculateVatAmount(values.amount, e.target.value));
                  }}
                  onBlur={handleBlur}
                  error={touched.vatId && !!errors.vatId}
                >
                  {taxRates.map((tax) => (
                    <MenuItem key={tax.id} value={tax.id}>
                      {tax.name} ({tax.rate}%)
                    </MenuItem>
                  ))}
                </Select>
                {touched.vatId && errors.vatId && (
                  <Typography color="error">{errors.vatId}</Typography>
                )}
              </FormControl>

              {/* VAT Amount (Read-only) */}
              <TextField
                fullWidth
                variant="filled"
                label="VAT Amount"
                name="vatAmount"
                type="number"
                value={calculateVatAmount(values.amount, values.vatId)}
                InputProps={{ readOnly: true }}
                disabled
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Submitting..."
                  : props.isEditing
                  ? "Update"
                  : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ClientReceiptLinesForm;